import { Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { FundraisingType, routes } from '@blockpulse3/data/shared';
import { useCreateFundraisingMutation, useGetCompanyQuery } from '@blockpulse3/graphql/hooks';

import { privateFundraisingParametersDefaults } from '../../../utils';
import { IPrivateFundraisingParametersForm } from '../types';
import { NewPrivateFundraisingInformations } from './NewPrivateFundraisingInformations';

type Props = unknown;

/**
 * NewPrivateFundraisingCreate.
 * First step of private fundraising funnel.
 *
 * @returns {JSX.Element}
 */
export function NewPrivateFundraisingCreate(): JSX.Element {
  const navigate = useNavigate();

  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const isWorkspaceLoaded = !!company;

  const [createFundraising, { loading }] = useCreateFundraisingMutation();

  const defaultValues = useMemo(() => {
    return company && privateFundraisingParametersDefaults(company);
  }, [company]);

  const handleFormSubmit: SubmitHandler<IPrivateFundraisingParametersForm> = (data): void => {
    if (company) {
      const selectedHoldingMethods = data.allowedHoldingMethods.map(
        (holdingMethod) => holdingMethod.value,
      );

      createFundraising({
        variables: {
          createFundraisingInput: {
            name: data.name,
            companyId,
            pricePerShare: data.pricePerShare,
            subscriptionPeriod: data.subscriptionPeriod.value,
            assetType: data.assetType,
            allowedHoldingMethods: selectedHoldingMethods,
            type: FundraisingType.PRIVATE,
          },
        },
        onCompleted: (data) => {
          if (company) {
            /* ** Remove /create from history when we go back ** */
            window.history.replaceState(
              null,
              '',
              routes.workspace.fundraising.private.parameters.fmt(
                companyId,
                data.createFundraising.id,
              ),
            );
            navigate(
              routes.workspace.fundraising.private.documentation.fmt(
                companyId,
                data.createFundraising.id,
              ),
            );
          }
        },
      });
    }
  };

  return (
    <Skeleton isLoaded={isWorkspaceLoaded} w="full">
      <NewPrivateFundraisingInformations
        defaultValues={defaultValues}
        isLoading={loading}
        onSubmit={handleFormSubmit}
      />
    </Skeleton>
  );
}

export type NewPrivateFundraisingCreateProps = Props;
